import { parsePatientNameString } from "../dicom-definitions";
import Handlebars from "handlebars/dist/handlebars.js"
import { ToFixedPipe } from "../to-fixed.pipe";

export function createHandlebars() {
  const handlebars = Handlebars.create();
  const toFixed = new ToFixedPipe();
    
  handlebars.Utils.escapeExpression = function(str: any) {
    if (typeof str === 'string')
    return str.replace(/[&%$#_\{\}~\^\\]/g, "\\$&");
    return str;
  };
  

  handlebars.registerHelper("tex", function(str: string) {
    return str.replace(/[&%$#_\{\}~\^\\]/g, "\\$&");
  });


  handlebars.registerHelper('let', function(value, options) {
    const variableName = options.hash.as;
    if (!variableName) {
    throw new Error("Missing 'as' parameter in 'let' helper");
    }

    // Create a new context with the variable
    const context = Object.assign({}, this);
    context[variableName] = value;

    return options.fn(context);
  });


  handlebars.registerHelper("numeric", (val: number) => {
    if (typeof val !== 'number')
    return val;

    if (val > 10)
    return toFixed.transform(val, 2);

    return toFixed.transform(val, 4);
  });

  handlebars.registerHelper("formatDateTime", function(datetime: Date) {
    if (!datetime)
    return "";

    // Trick to use toISOString(), but with local time
    const date = new Date(datetime);
    date.setUTCFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    const dateStr = date.toISOString();
    return dateStr.substring(0, 10) + ' ' + dateStr.substring(11, 19);
  });

  handlebars.registerHelper("formatDate", function(datetime: Date) {
    if (!datetime)
    return "";
    
    // Trick to use toISOString(), but with local time
    const date = new Date(datetime);
    date.setUTCFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    const dateStr = date.toISOString();
    return dateStr.substring(0, 10);
  });

  handlebars.registerHelper("dicomPatientName", function(fulltext: string) {
    if (!fulltext)
    return "";

    let parsed = parsePatientNameString(fulltext);
    
    let formatted = texHyphenate(parsed.alphabetic.lastName);
    if (parsed.alphabetic.firstName) {
    formatted += ", " + texHyphenate(parsed.alphabetic.firstName);

    if (parsed.alphabetic.middleName)
      formatted += " " + texHyphenate(parsed.alphabetic.middleName);
    }

    return formatted;
  });

  handlebars.registerHelper("confidence", function(value: number) {
    if (value === null || value === undefined)
    return '';

    if (value > 0.75)
    return $localize `High`;
    else if (value > 0.25)
    return $localize `Moderate`;
    else
    return $localize `Low`;
  });

  handlebars.registerHelper("pct", function(value: number) {
    let pct: string;

    if (value === null || value === undefined)
    pct = "?";
    else
    pct = (100 * value).toFixed(0);

    return pct + "%";
  });

  handlebars.registerHelper("chartbar", function(color: string, probability: number, certainty: number) {
    const barwidth = 3;

    return new Handlebars.SafeString(
    `\\begin{tikzpicture}
    \\draw[draw=white,transparent] (0,0) rectangle (${barwidth},0.55);
    \\filldraw[fill=${color},draw=${color}] (0,0.35) rectangle ++(${probability * certainty * barwidth},-0.3);
    \\draw[dashed,draw=${color}] (${probability * certainty * barwidth},0.35) rectangle ++(${probability * (1-certainty) * barwidth},-0.3);
    \\end{tikzpicture}`
    );
  });

  handlebars.registerHelper("times", function(n, block) {
    let result = "";
    for (let i = 0; i < n; i++) {
    result += block.fn(i);
    }
    return result;
  });

  handlebars.registerHelper('add', function(...args) {
    // The last argument is the Handlebars options object
    const options = args.pop();

    // Sum all numeric arguments
    const sum = args.reduce((acc, val) => {
    const num = parseFloat(val);
    return acc + (isNaN(num) ? 0 : num);
    }, 0);

    return sum;
  });


  handlebars.registerHelper('formatDynamicRegionFeatureName', function(regionName, dynamicRegionIndex, featurePath) {
    const paddedIndex = String(dynamicRegionIndex).padStart(2, '0');
    return `${regionName}$${paddedIndex}.${featurePath}`;
  });

  handlebars.registerHelper('switch', function(value, options) {
    options.data.switch_value = value;
    options.data.switch_break = false;
    return options.fn(this);
  });

  handlebars.registerHelper('case', function(value, options) {
    if (value == options.data.switch_value) {
      options.data.switch_break = true;
      return options.fn(this);
    }
  });

  handlebars.registerHelper('default', function(options) {
    if (options.data.switch_break == false) {
      return options.fn(this);
    }
  });

  handlebars.registerHelper('array', function(...args) {
    return args.slice(0, -1); // remove options object
  });

  handlebars.registerHelper('object', function(...args) {
    const options = args.pop();
    const obj = {};
    for (let i = 0; i < args.length; i += 2) {
    obj[args[i]] = args[i + 1];
    }
    return obj;
  });

  handlebars.registerHelper('ifCond', function (v1, operator, v2, options) {

    switch (operator) {
      case '==':
        return (v1 == v2) ? options.fn(this) : options.inverse(this);
      case '===':
        return (v1 === v2) ? options.fn(this) : options.inverse(this);
      case '!=':
        return (v1 != v2) ? options.fn(this) : options.inverse(this);
      case '!==':
        return (v1 !== v2) ? options.fn(this) : options.inverse(this);
      case '<':
        return (v1 < v2) ? options.fn(this) : options.inverse(this);
      case '<=':
        return (v1 <= v2) ? options.fn(this) : options.inverse(this);
      case '>':
        return (v1 > v2) ? options.fn(this) : options.inverse(this);
      case '>=':
        return (v1 >= v2) ? options.fn(this) : options.inverse(this);
      case '&&':
        return (v1 && v2) ? options.fn(this) : options.inverse(this);
      case '||':
        return (v1 || v2) ? options.fn(this) : options.inverse(this);
      default:
        return options.inverse(this);
    }
  });

  handlebars.registerHelper({
    eq: (v1, v2) => v1 === v2,
    ne: (v1, v2) => v1 !== v2,
    lt: (v1, v2) => v1 < v2,
    gt: (v1, v2) => v1 > v2,
    lte: (v1, v2) => v1 <= v2,
    gte: (v1, v2) => v1 >= v2,
    and() {
      return Array.prototype.every.call(arguments, Boolean);
    },
    or() {
      return Array.prototype.slice.call(arguments, 0, -1).some(Boolean);
    }
  });

  handlebars.registerHelper('fallback', function(value, fallback) {
    if (value === null || value === undefined || value === "")
    return fallback;
    return value;
  });

  handlebars.registerHelper('ifIsNthItem', function(options) {
    var index = options.data.index + 1,
      nth = options.hash.nth;
  
    if (index % nth === 0) 
    return options.fn(this);
    else
    return options.inverse(this);
  });

  handlebars.registerHelper('renderName', function(regionName: string, options) {
    // Underscores are difficult in LaTeX, so we always replace them with hyphens in filenames
    let fileName = regionName.replace(/_/g, "-");

    const { dynamicIndex, camera, ext } = options.hash;
    if (dynamicIndex !== undefined && dynamicIndex !== null) {
    const paddedIndex = String(dynamicIndex).padStart(2, '0');
    fileName += `${paddedIndex}`;
    }
    if (camera)
    fileName += `-${camera}`;

    return fileName + '.' + (ext || "jpg");
  });

  return handlebars;
}

function texHyphenate(word: string): string {
  if (word.length < 15)
  return word;

  return word.match(/(.{1,10})/g).join('\-');
}
